/**
 * Offline queue for gratitude entries.
 * Saves to IndexedDB when network is unavailable, syncs when back online.
 */
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { createLogger } from '@/lib/logger';
const log = createLogger('offlineQueue');

const DB_NAME = "thnx_sync_queue";
const DB_VERSION = 2; // bump to add gratitudes store
const STORE_NAME = "gratitudes";
const SYNC_TAG = "thnx-gratitude-sync";

interface PendingGratitude {
  id?: number;
  user_id: string;
  content: string;
  practice_type: string;
  category: string;
  input_method: string;
  voice_recording_url?: string | null;
  voice_duration_seconds?: number | null;
  entry_date: string;
  created_at: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // Legacy store from onboarding sync
      if (!db.objectStoreNames.contains("requests")) {
        db.createObjectStore("requests", { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function queueGratitude(entry: PendingGratitude): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).add(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getPendingGratitudes(): Promise<PendingGratitude[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function deletePendingGratitude(id: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function registerSync() {
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    navigator.serviceWorker.ready.then((reg) => {
      (reg as any).sync.register(SYNC_TAG).catch(() => {
        // Background sync not supported — fallback to online listener
      });
    });
  }
}

/**
 * Classify whether an error is transient (network/offline/5xx) vs permanent
 * (validation/auth). Permanent failures should not stop the whole queue.
 */
function isTransientError(error: { message?: string; code?: string } | null | undefined): boolean {
  if (!error) return false;
  const msg = error.message?.toLowerCase() ?? "";
  return (
    !navigator.onLine ||
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("timeout") ||
    error.code === "PGRST301" // statement timeout
  );
}

/**
 * Attempt to flush all pending gratitudes to Supabase.
 * Called on app load and when connectivity returns.
 *
 * Strategy:
 *  - Transient errors (offline/network) halt the flush — we'll retry on next online event
 *  - Permanent errors (e.g. invalid data) skip that entry but keep trying others
 *    after 3 consecutive attempts, so a single bad entry doesn't block the queue
 */
export async function flushPendingGratitudes(): Promise<number> {
  let synced = 0;
  try {
    const pending = await getPendingGratitudes();
    if (pending.length === 0) return 0;

    for (const entry of pending) {
      const entryId = entry.id!;
      // Use encrypted insert RPC
      const { error } = await supabase.rpc("insert_gratitude", {
        p_user_id: entry.user_id,
        p_content: entry.content,
        p_category: entry.category,
        p_practice_type: entry.practice_type,
        p_input_method: entry.input_method,
        p_voice_recording_url: entry.voice_recording_url || null,
        p_voice_duration_seconds: entry.voice_duration_seconds || null,
        p_entry_date: entry.entry_date,
      });
      if (!error) {
        await deletePendingGratitude(entryId);
        synced++;
        continue;
      }

      if (isTransientError(error)) {
        // Stop on transient failures — we're likely offline again. Retry later.
        log.warn("[offlineQueue] transient error, stopping flush:", error);
        break;
      }

      // Permanent error — log but keep trying other entries
      log.error("[offlineQueue] permanent error on entry, skipping:", entryId, error);
    }

    if (synced > 0) {
      toast.success(`Synced ${synced} offline gratitude${synced > 1 ? "s" : ""}`, {
        description: "Your entries are now saved to the cloud ☁️",
      });
    }
  } catch (e) {
    log.warn("[offlineQueue] flush error:", e);
  }
  return synced;
}

/**
 * Insert a gratitude entry with offline fallback.
 * Returns the inserted entry if online, or null if queued offline.
 */
export async function saveGratitudeWithOfflineFallback(entry: {
  user_id: string;
  content: string;
  practice_type: string;
  category: string;
  input_method: string;
  voice_recording_url?: string | null;
  voice_duration_seconds?: number | null;
}): Promise<{ data: any; offline: boolean; error: any }> {
  const payload = {
    ...entry,
    entry_date: new Date().toISOString().split("T")[0],
    created_at: new Date().toISOString(),
  };

  // Try network insert first via encrypted RPC
  try {
    const { data, error } = await supabase.rpc("insert_gratitude", {
      p_user_id: entry.user_id,
      p_content: entry.content,
      p_category: entry.category,
      p_practice_type: entry.practice_type,
      p_input_method: entry.input_method,
      p_voice_recording_url: entry.voice_recording_url || null,
      p_voice_duration_seconds: entry.voice_duration_seconds || null,
    });

    if (!error) {
      return { data, offline: false, error: null };
    }

    // If it's a network error, fall through to offline queue
    if (!navigator.onLine || error.message?.includes("Failed to fetch") || error.message?.includes("NetworkError")) {
      await queueGratitude(payload);
      registerSync();
      toast.info("Saved offline", {
        description: "Will sync when you're back online ☁️",
      });
      return { data: null, offline: true, error: null };
    }

    // Other Supabase error — return it
    return { data: null, offline: false, error };
  } catch (e: any) {
    // Network completely unavailable
    if (!navigator.onLine || e?.message?.includes("Failed to fetch") || e?.message?.includes("NetworkError")) {
      await queueGratitude(payload);
      registerSync();
      toast.info("Saved offline", {
        description: "Will sync when you're back online ☁️",
      });
      return { data: null, offline: true, error: null };
    }
    return { data: null, offline: false, error: e };
  }
}

/**
 * Initialize online listener to auto-flush when connectivity returns.
 */
export function initOfflineSync() {
  window.addEventListener("online", () => {
    flushPendingGratitudes();
  });

  // Also try flushing on app load in case there are pending entries
  flushPendingGratitudes();
}
